import React from "react";
import { useI18n } from "@/lib/i18n";

/**
 * Vertrauensring – animated SVG donut with 4 segments.
 * segments: { vollstaendigkeit, aktualitaet, sicherheit, notfallbereitschaft } (0–100)
 */
export default function TrustRing({ total = 0, segments = {}, weeklySentence = "", size = 280 }) {
  const { t } = useI18n();
  const stroke = 18;
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  // Each quarter of the ring represents one segment, length scaled to segment %
  const segKeys = ["vollstaendigkeit", "aktualitaet", "sicherheit", "notfallbereitschaft"];
  const colors = ["#10B981", "#F59E0B", "#0F172A", "#334155"];
  const quarter = circumference / 4;
  const gap = 6;

  return (
    <div className="flex flex-col items-center" data-testid="trust-ring">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Background tracks */}
          {segKeys.map((_, i) => {
            const dashArray = `${quarter - gap} ${circumference}`;
            const dashOffset = -(i * quarter) + gap / 2;
            return (
              <circle
                key={`bg-${i}`}
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke="#E2E8F0"
                strokeWidth={stroke}
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
              />
            );
          })}
          {/* Foreground arcs */}
          {segKeys.map((key, i) => {
            const value = Math.max(0, Math.min(100, segments[key] ?? 0));
            const arc = (quarter - gap) * (value / 100);
            const dashArray = `${arc} ${circumference}`;
            const dashOffset = -(i * quarter) + gap / 2;
            return (
              <circle
                key={`fg-${i}`}
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={colors[i]}
                strokeWidth={stroke}
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                style={{ transition: "stroke-dasharray 900ms ease-out" }}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center trust-ring-center rounded-full" style={{ margin: stroke * 1.5 }}>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">{t.dashboard.trustRing}</div>
          <div className="font-serif font-semibold text-[56px] leading-none text-ag-ink" data-testid="trust-ring-total">{total}%</div>
          {weeklySentence ? (
            <div className="mt-3 text-xs text-emerald-700 font-medium max-w-[180px] text-center leading-snug" data-testid="trust-ring-weekly">
              {weeklySentence}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-8 w-full max-w-md">
        {segKeys.map((key, i) => (
          <div key={key} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors[i] }} />
            <div className="flex-1 flex items-center justify-between">
              <span className="text-slate-700">{t.dashboard.segments[key]}</span>
              <span className="font-medium text-ag-ink tabular-nums">{segments[key] ?? 0}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
